import PropTypes from "prop-types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ellipsis, Link2, SquareM, SquarePen, Trash2 } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RegistrantLeadStatusModal from "routes/leads/swoogo_registrants/RegistrantLeadStatusModal";
import ConfirmRemoveSessionRegistrantsDialog from "routes/swoogo/ConfirmRemoveSessionRegistrantsDialog";
import LinkContactDialog from "routes/swoogo/LinkContactDialog";
import RemoveSessionRegistrantsDialog from "routes/swoogo/RemoveSessionRegistrantsDialog";

const RegistrantActions = ({
  registrant,
  sessionId,
  canLink,
  canEdit,
  canSetLeadStatus,
  canRemoveSessionRegistrants,
}) => {
  const navigate = useNavigate();
  const [activeDialog, setActiveDialog] = useState(null);
  const hasContact = registrant.contact_id != null;
  const hasOtherActions = canEdit || canLink || canSetLeadStatus;
  const sessions = registrant.sessions ?? [];
  const hasSessionToRemove =
    sessionId == null
      ? sessions.length > 0
      : sessions.some(session => session.id === sessionId);

  const handleOpenChange = isOpen => {
    if (!isOpen) {
      setActiveDialog(null);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Registrant actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-auto">
          {canEdit && (
            <DropdownMenuItem
              onClick={() =>
                navigate(`/swoogo/registrants/${registrant.id}/edit`)
              }
            >
              <SquarePen />
              Edit Registrant
            </DropdownMenuItem>
          )}
          {canLink && (
            <DropdownMenuItem onClick={() => setActiveDialog("link")}>
              <Link2 />
              {hasContact ? "Change Contact" : "Link Contact"}
            </DropdownMenuItem>
          )}
          {canSetLeadStatus && (
            <DropdownMenuItem onClick={() => setActiveDialog("leadStatus")}>
              <SquareM />
              Set Meta Status
            </DropdownMenuItem>
          )}
          {hasOtherActions && canRemoveSessionRegistrants && (
            <DropdownMenuSeparator />
          )}
          {canRemoveSessionRegistrants && (
            <DropdownMenuItem
              variant="destructive"
              disabled={!hasSessionToRemove}
              onClick={() => setActiveDialog("removeSessions")}
            >
              <Trash2 />
              {sessionId == null
                ? "Remove from Sessions"
                : "Remove from Session"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {activeDialog === "link" && (
        <LinkContactDialog
          registrant={registrant}
          open
          onOpenChange={handleOpenChange}
        />
      )}
      {activeDialog === "leadStatus" && (
        <RegistrantLeadStatusModal
          registrantIds={[registrant.id]}
          open
          onOpenChange={handleOpenChange}
        />
      )}
      {activeDialog === "removeSessions" &&
        (sessionId == null ? (
          <RemoveSessionRegistrantsDialog
            registrants={[registrant]}
            onOpenChange={handleOpenChange}
          />
        ) : (
          <ConfirmRemoveSessionRegistrantsDialog
            registrants={[registrant]}
            sessionId={sessionId}
            onOpenChange={handleOpenChange}
          />
        ))}
    </>
  );
};

RegistrantActions.propTypes = {
  registrant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    contact_id: PropTypes.number,
    contact_name: PropTypes.string,
    sessions: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
    ),
  }).isRequired,
  sessionId: PropTypes.number,
  canLink: PropTypes.bool,
  canEdit: PropTypes.bool,
  canSetLeadStatus: PropTypes.bool,
  canRemoveSessionRegistrants: PropTypes.bool,
};

export default RegistrantActions;
